import React, {ReactNode, useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import classNames from "classnames";
import {RootState} from "../../../reducers";
import {getStoreState} from "../../services/Store/reducer";
import NavbarMenus from "./NavbarMenus";
import NavbarUser from "./NavbarUser";
import {ReactComponent as BarsIco} from "../../../images/icons/bars.svg";
import Drawer from "./Drawer/Drawer";
import {selectLocale} from "../../services/Session/reducer";
import StoreUtils from "../../services/Store/StoreUtils";
import NavbarPlaces from "./NavbarPlaces";
import AppConfig from "../../services/AppConfig";
import NavbarVouchers from "./NavbarVouchers";
import {useMainStickyNav} from "../../pages/Contexts/MainNavStickyContext";
import NavbarBackMultistore from "./NavbarBackMultistore";
import NavbarSearch from "./NavbarSearch";
import ResponsiveComponent from "../Common/ResponsiveComponent";
import NavbarBack from "./NavbarBack";

interface OwnProps {
    withoutBackComponents?: true;
    navbarBackTitle?: string;
    navbarBackUrl?: string;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

function Navbar(props: Props) {
    const [pagesCollapse, setPagesCollapse] = useState(false);
    const [drawer, setDrawer] = useState(false);
    const config = props.store_state.config;
    const {t} = useTranslation();
    const {mainNavSticky} = useMainStickyNav();
    const isHomepageMenu = StoreUtils.isHomepageMenu(config);
    const isMultistore = StoreUtils.isMultistore(props.store_state.default_config);

    if (!config) return null;

    const configLayout = config.layout;
    if (!configLayout) return null;

    const anyMenuLinks = configLayout?.links?.some(x => x.type === "MENU");

    const renderLogoView = () => {
        if (configLayout?.image_link) {
            return (
                <img alt={t("common.word.location_logo")} src={configLayout.image_link.default}/>
            )
        }
        return (
            <div className={"logo-text"}>
                {config?.name}
            </div>
        );
    }

    const renderNavbarBack = () => {
        const component = (
            <>
                <NavbarBackMultistore config={props.store_state.config}
                                      multistoreConfig={props.store_state.default_config}/>
                <NavbarPlaces config={props.store_state.config}
                              multistoreConfig={props.store_state.default_config}/>
            </>
        );

        if (isMultistore) return component;

        if (props.withoutBackComponents) return <></>;

        if (props.navbarBackTitle && props.navbarBackUrl && !isHomepageMenu) return (
            <NavbarBack title={props.navbarBackTitle} link={props.navbarBackUrl}/>
        );

        return <></>;
    }

    const renderLogo = () => {
        const component = (
            <Link to="/" className="navbar-brand">
                {renderLogoView()}
            </Link>
        );

        if (props.withoutBackComponents || isMultistore || isHomepageMenu) return component;

        if (!isHomepageMenu) return <></>;

        return <ResponsiveComponent small={<></>} large={component} breakpoint={768}/>
    }

    const getNavClassName = (smallDevices: boolean) => {
        const className = `navbar navbar-expand-lg navbar-light ${mainNavSticky ? "sticky-top" : ""}`;
        const multiStoreClassName = "navbar-multistore";

        if (isMultistore) return `${className} ${multiStoreClassName}`;

        if (props.withoutBackComponents) return className;

        if (!isHomepageMenu) {
            if (smallDevices) return `${className} ${multiStoreClassName}`;
            return className;
        }

        return className;
    }

    const getNavComponent = (className: string, children: ReactNode) => {
        return (
            <nav
                className={className}
                id="main-navbar">
                {children}
            </nav>
        )
    }

    const renderNav = (children: ReactNode) => {
        return <ResponsiveComponent small={getNavComponent(getNavClassName(true), children)}
                                    large={getNavComponent(getNavClassName(false), children)}
                                    breakpoint={768}/>;
    }

    if (AppConfig.isNativeMobile()) {
        return renderNav(
            <div className="navbar-brand-container" id="navbar-brand-container">
                {renderNavbarBack()}
            </div>
        );
    }

    const handleOpenDrawer = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawer(!drawer);
        event.currentTarget.blur();
    }

    return (
        <>
            {renderNav(
                <div className="navbar-brand-container" id="navbar-brand-container">
                    {renderLogo()}
                    {renderNavbarBack()}
                    <div className="navbar-options" id='navbar-options'>
                        <NavbarSearch/>
                        <NavbarVouchers/>
                        <NavbarUser/>
                        {/*<NavbarLanguages languages={config.languages} locale={props.locale}/>*/}
                        <button className="btn-icon navbar-toggler" type="button"
                                aria-label={t("common.word.menu")}
                                onClick={handleOpenDrawer}>
                            <BarsIco aria-hidden="true"/>
                        </button>
                    </div>
                    {(anyMenuLinks) && (
                        <div className={classNames(`navbar-menus collapse navbar-collapse`, {
                            'show': pagesCollapse
                        })} id="navbarNav">
                            {anyMenuLinks && (
                                <NavbarMenus links={configLayout?.links}/>
                            )}
                        </div>
                    )}
                </div>
            )}
            <Drawer config={props.store_state.config} onHide={() => setDrawer(false)} show={drawer}/>
        </>
    )
}


const mapStateToProps = (state: RootState) => ({
    store_state: getStoreState(state.config),
    locale: selectLocale(state.session)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(Navbar));